import type { Edge, Node } from '@xyflow/react'
import { computeAllDisabledUpstream } from '@/features/backtest/utils/upstream-traversal'
import { isEditorOnlyNodeType } from '@/features/backtest/utils/editor-only-nodes'

// Deactivating a merge/AND input should remove that whole branch from the run.
// `computeAllDisabledUpstream` identifies, on the canonical graph:
//   • `disabledNodeIds`/`disabledEdgeIds` — upstream nodes that ONLY feed
//     disabled inputs, and the disabled input edges themselves; and
//   • `deadMergeNodeIds`/`deadOutputEdgeIds` — a merge whose EVERY input is
//     disabled (its output means nothing) plus its output edge(s).
// We strip all four from the run payload so the engine never receives a dead
// merge — which would otherwise raise "No incoming edges found" (context.py),
// since its input branch was pruned but the merge itself was still sent. The
// consumer keeps its remaining live inputs. Works on the canonical graph, so
// it's identical whether a subnet is open or collapsed.
//
// Shared by every path that ships a graph to the engine: the single-run and
// preview payloads in use-backtest-execution, and the batch payload in
// use-submit-batch-run. Keeping it here (not in the run hook) lets every
// caller import it without pulling in the hook.
export function pruneDisabledStreams(
  cleaned: { nodes: Array<Record<string, unknown>>; edges: Array<Record<string, unknown>> },
  rawNodes: Node[],
  rawEdges: Edge[],
): { nodes: Array<Record<string, unknown>>; edges: Array<Record<string, unknown>> } {
  // Compute on the CANONICAL graph (drop proxy edges) so the disabled branch is
  // identified the same whether a subnet is open or collapsed.
  const canonicalEdges = rawEdges.filter(
    (e) => (e.data as { proxy?: boolean } | undefined)?.proxy !== true,
  )
  const { disabledNodeIds, disabledEdgeIds, deadMergeNodeIds, deadOutputEdgeIds } =
    computeAllDisabledUpstream(rawNodes, canonicalEdges)

  const prunedNodeIds = new Set([...disabledNodeIds, ...deadMergeNodeIds])
  const prunedEdgeIds = new Set([...disabledEdgeIds, ...deadOutputEdgeIds])
  if (prunedNodeIds.size === 0 && prunedEdgeIds.size === 0) return cleaned

  return {
    nodes: cleaned.nodes.filter((n) => !prunedNodeIds.has(n.id as string)),
    edges: cleaned.edges.filter(
      (e) =>
        !prunedEdgeIds.has(e.id as string) &&
        !prunedNodeIds.has(e.source as string) &&
        !prunedNodeIds.has(e.target as string),
    ),
  }
}

/**
 * Remove nodes the editor owns but the engine has never heard of, plus every
 * edge touching them. The engine deserializes by node type, so an unknown type
 * fails the whole run, and an edge left pointing at a removed node fails on the
 * endpoint. Both go together or neither is safe.
 */
export function stripEditorOnlyNodes(
  cleaned: {
    nodes: Array<Record<string, unknown>>
    edges: Array<Record<string, unknown>>
  },
  /** Editor-only types to leave in (the EdgeScript printer keeps the Universe). */
  keep: ReadonlySet<string> = new Set(),
): { nodes: Array<Record<string, unknown>>; edges: Array<Record<string, unknown>> } {
  const strippedIds = new Set(
    cleaned.nodes
      .filter((n) => isEditorOnlyNodeType(n.type as string) && !keep.has(n.type as string))
      .map((n) => n.id as string),
  )
  if (strippedIds.size === 0) return cleaned

  return {
    nodes: cleaned.nodes.filter((n) => !strippedIds.has(n.id as string)),
    edges: cleaned.edges.filter(
      (e) => !strippedIds.has(e.source as string) && !strippedIds.has(e.target as string),
    ),
  }
}
